<template>
  <div v-if="displays.length" class="sun-tracker-panel space-y-3">
    <div class="flex items-center justify-between gap-3">
      <div>
        <h4 class="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
          {{ tt('Sun Controller', 'Sončni krmilnik') }}
        </h4>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{
            tt(
              'Live sun position, season × occupancy cell and position source',
              'Trenutni položaj sonca, celica letni čas × prisotnost in vir položaja',
            )
          }}
        </p>
      </div>
    </div>

    <article
      v-for="display in displays"
      :key="display.automationId"
      class="rounded-3xl border border-amber-200 bg-amber-50/40 p-4 shadow-sm dark:border-amber-900/50 dark:bg-amber-500/5">
      <!-- Header row: sun badge + name/status + active cell -->
      <div class="flex items-center gap-3">
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors"
          :class="
            display.sunActive
              ? 'bg-amber-500/20 text-amber-500'
              : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
          ">
          <ControlIcon name="warm" size="1.25rem" />
        </div>

        <div class="min-w-0 flex-1">
          <h5 class="truncate text-base font-semibold text-gray-900 dark:text-white">{{ display.name }}</h5>
          <p class="truncate text-sm text-gray-600 dark:text-gray-300">{{ statusLabel(display) }}</p>
        </div>

        <div class="shrink-0 rounded-2xl bg-amber-100/70 px-3 py-2 text-right dark:bg-amber-500/10">
          <div class="text-lg font-semibold leading-none text-amber-600 dark:text-amber-300">
            {{ activeCellLabel(display) }}
          </div>
          <div class="mt-1 text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
            {{ sourceLabel(display) }}
          </div>
        </div>
      </div>

      <!-- Live sun info (full width row) -->
      <div class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
        <span style="font-variant-numeric: tabular-nums">
          <span class="text-amber-500"><ControlIcon name="warm" size="0.875em" /></span>
          {{ tt('Az', 'Azimut') }} {{ liveAzimuth }}
        </span>
        <span>{{ tt('Altitude', 'Višina') }} {{ liveAltitude }}</span>
        <span>{{ tt('Rise', 'Vzhod') }} {{ liveSunrise }}</span>
        <span>{{ tt('Set', 'Zahod') }} {{ liveSunset }}</span>
        <span
          class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
          :class="
            display.sunInWindow
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
              : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
          ">
          {{ tt('Sun in window', 'Sonce v oknu') }}: {{ display.sunInWindow ? tt('yes', 'da') : tt('no', 'ne') }}
        </span>
      </div>

      <!-- Enabled + full settings (full width row) — CONFIGURATION, so the
           editor, the enable switch and the Save button are the admin's alone
           (cards t_bd611e14 / t_a4368691; D12: settings hidden, control
           shown). A non-admin keeps the status display above. -->
      <template v-if="isAdmin">
        <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
          <label class="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-300">
            <span>{{ tt('Enabled', 'Omogočeno') }}</span>
            <span class="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                class="sr-only peer"
                :checked="draftConfig(display.automationId).enabled !== false"
                @change="toggleEnabled(display.automationId, ($event.target as HTMLInputElement).checked)" />
              <div
                class="w-9 h-5 bg-gray-200 dark:bg-gray-600 peer-checked:bg-amber-500 rounded-full relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
            </span>
          </label>

          <button
            type="button"
            class="flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700 dark:text-amber-400"
            @click="toggleExpanded(display.automationId)">
            <ControlIcon :name="isExpanded(display.automationId) ? 'chevron-up' : 'chevron-down'" size="0.875rem" />
            {{
              isExpanded(display.automationId)
                ? tt('Hide full settings', 'Skrij vse nastavitve')
                : tt('Edit full settings', 'Uredi vse nastavitve')
            }}
          </button>
        </div>

        <div
          v-if="isExpanded(display.automationId)"
          class="mt-2 rounded-2xl border border-gray-200 bg-white/80 p-3 dark:border-gray-700 dark:bg-gray-900/60">
          <SunTrackerEditor
            :model-value="draftConfig(display.automationId)"
            @update:model-value="setDraftConfig(display.automationId, $event)" />
        </div>

        <!-- Save -->
        <div class="mt-4 flex justify-end">
          <button
            type="button"
            class="rounded-full px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
            :class="isDirty(display) ? 'bg-amber-600 hover:bg-amber-700' : 'bg-gray-300 dark:bg-gray-700'"
            :disabled="!isDirty(display) || isSaving(display.automationId) || hasErrors(display)"
            @click="saveAutomation(display)">
            {{ isSaving(display.automationId) ? tt('Saving…', 'Shranjujem…') : tt('Save changes', 'Shrani spremembe') }}
          </button>
        </div>
      </template>
    </article>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { formatTime as formatTimeLocale } from '../../../utils/formatDate';
import { apiFetch } from '../../../services/api';
import { useAuthStore } from '../../../stores/auth';
import { useDeviceAutomationsStore } from '../../../stores/deviceAutomations';
import { useDeviceValues } from '../../../composables/useDeviceValues';
import { useSolar } from '../../../composables/useSolar';
import { useShadesStore } from '../../../stores/shades';
import { useShadeSeasonLabels } from '../../../composables/useShadeSeasonLabels';
import {
  SUN_SOURCE_LABELS,
  SunTrackerDisplay,
  sunLabel,
  buildSunTrackerDisplay,
  cellLabel,
  getSunTrackerErrors,
  normalizeSunTrackerConfig,
} from '../../../utils/sunTracker';
import SunTrackerEditor from './SunTrackerEditor.vue';
import ControlIcon from '../../device-icons/ControlIcon.vue';
import { tt } from '../../../utils/consoleText';

const props = defineProps<{
  deviceId: string;
}>();

const emit = defineEmits<{
  updated: [];
}>();

const store = useDeviceAutomationsStore();
const authStore = useAuthStore();
const { currentValues } = useDeviceValues(props.deviceId);
const solar = useSolar();
const shades = useShadesStore();
const seasonLabels = useShadeSeasonLabels();

/**
 * Editing an automation is *configuration* of the house (card `t_bd611e14`):
 * the hub serves every device-automation mutation `authenticate +
 * authorize("admin")`, so the editor, the enable switch and the Save button
 * belong to an admin (card `t_a4368691`; D12 — an automation schedule is
 * configuration, so the settings are not offered below that). The status
 * display above stays: the reads are `authenticate`, kept for exactly these
 * device sheets.
 */
const isAdmin = computed(() => authStore.isAdmin);

onMounted(() => {
  if (shades.seasonsSorted.length === 0) {
    void shades.fetchSeasons();
  }
});

const draftConfigs = reactive<Record<string, Record<string, unknown>>>({});
const configSignatures = reactive<Record<string, string>>({});
const savingIds = ref<string[]>([]);
const expandedIds = ref<string[]>([]);

const summaries = computed(() =>
  store
    .getDeviceAutomationSummaries(props.deviceId)
    .filter((automation) => automation.automationType === 'shade_sun_tracking'),
);

const displays = computed<SunTrackerDisplay[]>(() =>
  summaries.value.map((automation) => buildSunTrackerDisplay(automation, currentValues.value)),
);

// Seed dirty-tracked drafts whenever a summary's config changes.
watch(
  summaries,
  (list) => {
    for (const automation of list) {
      const normalized = normalizeSunTrackerConfig(automation.config);
      const signature = JSON.stringify(normalized);
      if (configSignatures[automation.id] !== signature) {
        draftConfigs[automation.id] = JSON.parse(signature);
        configSignatures[automation.id] = signature;
      }
    }
  },
  { immediate: true },
);

onMounted(() => {
  void store.fetchDeviceAutomationSummaries(props.deviceId);
});

// ── Draft helpers ────────────────────────────────────────────────────────

function draftConfig(automationId: string): Record<string, unknown> {
  if (!draftConfigs[automationId]) {
    draftConfigs[automationId] = {};
  }
  return draftConfigs[automationId];
}

function setDraftConfig(automationId: string, nextConfig: Record<string, unknown>) {
  draftConfigs[automationId] = JSON.parse(JSON.stringify(nextConfig));
}

function toggleEnabled(automationId: string, enabled: boolean) {
  const draft = draftConfig(automationId);
  draftConfigs[automationId] = { ...draft, enabled };
}

function isExpanded(automationId: string): boolean {
  return expandedIds.value.includes(automationId);
}

function toggleExpanded(automationId: string) {
  expandedIds.value = isExpanded(automationId)
    ? expandedIds.value.filter((id) => id !== automationId)
    : [...expandedIds.value, automationId];
}

// ── Live sun info ────────────────────────────────────────────────────────

const sunPosition = computed(() => solar.sunPosition.value);
const sunTimes = computed(() => solar.sunTimes.value);

const liveAzimuth = computed(() => (sunPosition.value ? `${Math.round(sunPosition.value.azimuth)}°` : '—'));
const liveAltitude = computed(() => (sunPosition.value ? `${Math.round(sunPosition.value.altitude)}°` : '—'));
const liveSunrise = computed(() => (sunTimes.value ? formatTime(sunTimes.value.sunrise) : '—'));
const liveSunset = computed(() => (sunTimes.value ? formatTime(sunTimes.value.sunset) : '—'));

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return formatTimeLocale(date, { hour: '2-digit', minute: '2-digit' });
}

// ── Labels / status ──────────────────────────────────────────────────────

function activeCellLabel(display: SunTrackerDisplay): string {
  if (!display.enabled) return tt('Disabled', 'Onemogočeno');
  if (display.activeSource === 'inhibited') return tt('Inhibited', 'Zadržano');
  return cellLabel(display.activeCell, seasonLabels.value);
}

function sourceLabel(display: SunTrackerDisplay): string {
  if (!display.enabled) return tt('Controller off', 'Krmilnik izklopljen');
  if (display.activeSource === 'inhibited') return tt('Wind / rain hold', 'Zadržano zaradi vetra / dežja');
  if (display.activeSource === 'disabled') return tt('Off', 'Izklopljeno');
  if (display.activeSource === 'none' || display.activeSource === null) {
    return display.sunInWindow ? tt('Sun in window', 'Sonce v oknu') : tt('Monitoring', 'Spremljanje');
  }
  const source = SUN_SOURCE_LABELS[display.activeSource as keyof typeof SUN_SOURCE_LABELS];
  return source ? sunLabel(source) : '—';
}

function statusLabel(display: SunTrackerDisplay): string {
  if (!display.enabled) return tt('Automation disabled', 'Avtomatizacija onemogočena');
  if (display.activeSource === 'inhibited')
    return tt('Held by wind/rain protection', 'Zadržano zaradi zaščite pred vetrom in dežjem');
  if (display.sunActive) return tt('Controller active', 'Krmilnik aktiven');
  return tt('Monitoring sun position', 'Spremljanje položaja sonca');
}

// ── Save flow ────────────────────────────────────────────────────────────

function isDirty(display: SunTrackerDisplay): boolean {
  return JSON.stringify(draftConfig(display.automationId)) !== (configSignatures[display.automationId] ?? '');
}

function hasErrors(display: SunTrackerDisplay): boolean {
  return getSunTrackerErrors(draftConfig(display.automationId), seasonLabels.value).length > 0;
}

function isSaving(automationId: string): boolean {
  return savingIds.value.includes(automationId);
}

async function saveAutomation(display: SunTrackerDisplay) {
  if (hasErrors(display)) return;

  const automationId = display.automationId;
  savingIds.value = [...savingIds.value, automationId];

  try {
    await apiFetch(`/device-automations/${automationId}`, {
      method: 'PUT',
      body: JSON.stringify({
        config: draftConfig(automationId),
      }),
    });
    emit('updated');
    await refresh();
  } catch (error) {
    console.error(`[SunTrackerPanel] Failed to save automation ${automationId}`, error);
  } finally {
    savingIds.value = savingIds.value.filter((id) => id !== automationId);
  }
}

async function refresh() {
  store.invalidate(props.deviceId);
  await Promise.all([
    store.fetchDeviceAutomationSummaries(props.deviceId, true),
    store.fetchDeviceAutomations(props.deviceId, true),
  ]);
}
</script>
